import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { PencilIcon } from "@heroicons/react/24/solid";
import { Select, SelectItem, Spinner, Tooltip } from "@heroui/react";
import { toast } from "sonner";
import { K1DataTable } from "../components/K1DataTable";
import { K1FundsModal } from "../components/K1FundsModal";
import K1FilterBar from "../components/K1FilterBar";
import { useK1DashboardContext } from "../context/K1DashboardProvider";
import {
  buildFundScopeParams,
  buildK1DownloadUrl,
  fundSelectionKey,
  k1Api,
  type K1FundSelection,
} from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";
import { getQuarterFromYYYYMM } from "../utils/quarters";

const NO_QUARTER_KEY = "__no_quarter__";

function buildDownloadUrl(
  selection: K1FundSelection,
  quarter: string | null,
  quarterIsNull: boolean,
): string {
  const params = buildFundScopeParams(selection);
  if (quarterIsNull) params.set("quarter_is_null", "true");
  else if (quarter) params.set("quarter_equals", quarter);
  return buildK1DownloadUrl("k1_email_logs", params);
}

function getApiErrorMessage(error: unknown): string {
  const anyError = error as {
    response?: { data?: { detail?: unknown; message?: unknown } };
  };
  const data = anyError.response?.data;
  const detail = data?.detail;

  if (typeof detail === "string" && detail.trim()) return detail;
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }
  return "Request failed";
}

type Row = Record<string, unknown>;

const text = (key: string, header: string, size = 140): ColumnDef<Row> => ({
  accessorKey: key,
  header,
  size,
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined) return "";
    return String(v);
  },
});

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function renderLongText(value: unknown, maxPreviewLength: number) {
  if (value === null || value === undefined) return "";
  const full = String(value);
  const preview =
    full.length > maxPreviewLength
      ? `${full.slice(0, maxPreviewLength)}...`
      : full;
  const tooltipText = full.length > 4000 ? `${full.slice(0, 4000)}...` : full;
  const lines = tooltipText.split("\n");
  return (
    <Tooltip
      delay={0}
      closeDelay={0}
      placement="bottom-start"
      content={
        <div className="max-w-[480px] max-h-[400px] overflow-y-auto py-1 px-0.5 text-[12px] leading-relaxed whitespace-pre-wrap">
          {lines.map((line, i) => (
            <span key={i}>
              {line}
              {i < lines.length - 1 && <br />}
            </span>
          ))}
        </div>
      }
    >
      <span className="cursor-default block max-w-[280px] truncate text-slate-600">
        {preview}
      </span>
    </Tooltip>
  );
}

export default function EmailLogsView() {
  const queryClient = useQueryClient();
  const { selectedFundIds, fundSelection } = useK1DashboardContext();
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<Row | null>(null);

  const effectiveSelection =
    selectedFundIds.length > 0 ? fundSelection : ({ mode: "all" } as const);
  const scopeKey = fundSelectionKey(effectiveSelection);

  const { data: quarters = [] } = useQuery({
    queryKey: ["k1", "email-log-quarters", scopeKey],
    queryFn: () => k1Api.getEmailLogQuarters(effectiveSelection),
    staleTime: 60_000,
  });

  const selectedSpecificQuarter =
    selectedQuarter && selectedQuarter !== NO_QUARTER_KEY
      ? selectedQuarter
      : null;
  const quarterIsNull = selectedQuarter === NO_QUARTER_KEY;
  const displayQuarter = quarterIsNull ? "None" : selectedSpecificQuarter;

  const { data, isLoading } = useQuery({
    queryKey: [
      "k1",
      "email-logs",
      scopeKey,
      selectedSpecificQuarter,
      quarterIsNull,
    ],
    queryFn: () =>
      k1Api.getEmailLogs(
        effectiveSelection,
        selectedSpecificQuarter,
        quarterIsNull,
      ),
    staleTime: 60_000,
  });

  const { data: allFundsData } = useQuery({
    queryKey: ["k1", "funds-slim"],
    queryFn: () => k1Api.getFunds({ slim: true }),
    enabled: Boolean(editingRow),
    staleTime: 300_000,
  });

  const updateEmailLogFund = useMutation({
    mutationFn: ({
      emailLogId,
      fundId,
    }: {
      emailLogId: number;
      fundId: number | null;
    }) => k1Api.updateEmailLogFund(emailLogId, { fund_id: fundId }),
    onSuccess: (_, variables) => {
      setEditingRow(null);
      void queryClient.invalidateQueries({
        queryKey: ["k1", "email-logs"],
      });
      toast.success(
        variables.fundId === null ? "Fund cleared" : "Fund assigned",
        { position: "bottom-left" },
      );
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error), { position: "bottom-left" });
    },
  });

  const columns = useMemo<ColumnDef<Row>[]>(
    () => [
      {
        accessorKey: "fund_name",
        header: "Fund",
        size: 240,
        cell: ({ getValue, row }) => {
          const fundName = asString(getValue());
          const fundId = asNumber(row.original["fund_id"]);
          const label = fundName ?? (fundId !== null ? `Fund #${fundId}` : null);
          return (
            <button
              type="button"
              onClick={() => setEditingRow(row.original as Row)}
              className="group flex w-full items-center gap-2 text-left"
              aria-label="Assign fund"
            >
              <span
                className={[
                  "truncate text-[12px]",
                  label
                    ? "font-medium text-slate-700"
                    : "font-normal text-slate-400",
                ].join(" ")}
              >
                {label ?? "Assign fund"}
              </span>
              <PencilIcon className="h-3.5 w-3.5 shrink-0 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          );
        },
      },
      {
        accessorKey: "quarter",
        header: "Quarter",
        size: 100,
        cell: ({ getValue }) => getQuarterFromYYYYMM(getValue()),
      },
      text("email_type", "Email type", 140),
      {
        accessorKey: "gmail_subject",
        header: "Subject",
        size: 320,
        cell: ({ getValue }) => renderLongText(getValue(), 120),
      },
      {
        accessorKey: "contact_addresses",
        header: "Recipients",
        size: 220,
        cell: ({ getValue }) => {
          const v = getValue();
          if (!Array.isArray(v) || v.length === 0) return "";
          const emails = (v as unknown[]).flatMap((c) => {
            if (typeof c === "string") return c ? [c.trim()] : [];
            return [];
          });
          if (emails.length === 0) return "";
          return (
            <div className="flex flex-col gap-0.5 py-0.5">
              {emails.map((email, i) => (
                <a
                  key={i}
                  href={`mailto:${encodeURIComponent(email)}`}
                  className="truncate text-[12px] text-blue-600 hover:text-blue-800 hover:underline"
                  title={email}
                >
                  {email}
                </a>
              ))}
            </div>
          );
        },
      },
      text("sendgrid_template_id", "Template ID", 160),
      {
        accessorKey: "email_body",
        header: "Body",
        size: 300,
        cell: ({ getValue }) => renderLongText(getValue(), 100),
      },
      text("sending_timestamp", "Sent at", 160),
    ],
    [],
  );

  const rows = useMemo(() => (data?.data ?? []) as Row[], [data]);
  const funds = (allFundsData?.data ?? []) as {
    id: number;
    fund_id: number;
    fund_name: string;
    latest_reporting_period?: string;
    manager?: string;
    vintage_year?: number;
  }[];
  const editingFundId = asNumber(editingRow?.fund_id);
  const editingSelection: K1FundSelection = editingFundId
    ? { mode: "ids", ids: [editingFundId] }
    : { mode: "ids", ids: [] };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <K1FilterBar hidePeriod />
        {quarters.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-400">Quarter</span>
            <Select
              size="sm"
              selectedKeys={
                selectedQuarter === NO_QUARTER_KEY
                  ? new Set([NO_QUARTER_KEY])
                  : selectedSpecificQuarter
                    ? new Set([selectedSpecificQuarter])
                    : new Set()
              }
              onSelectionChange={(keys) =>
                setSelectedQuarter((Array.from(keys)[0] as string) ?? null)
              }
              aria-label="Email logs quarter"
              classNames={{
                trigger:
                  "h-8 min-h-8 w-28 shadow-none !rounded-md border-slate-200 text-[12px]",
                value: "text-[12px]",
              }}
            >
              <>
                {quarters.map((q) => (
                  <SelectItem key={q}>{q}</SelectItem>
                ))}
                <SelectItem key={NO_QUARTER_KEY}>None</SelectItem>
              </>
            </Select>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <h1 className="font-semibold text-xl text-primary">
              Emails
              {displayQuarter && (
                <span className="ml-2 text-sm font-normal text-slate-400">
                  — {displayQuarter}
                </span>
              )}
            </h1>
            <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
              {rows.length.toLocaleString()} emails
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <K1DataTable<Row, unknown>
              table_name="k1-email-logs"
              columns={columns}
              data={rows}
              getRowClassName={() => "hover:!bg-slate-50"}
              pinnedLeft={["fund_name"]}
              noCard
            />
            <div className="flex items-center border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildDownloadUrl(
                  effectiveSelection,
                  selectedSpecificQuarter,
                  quarterIsNull,
                )}
                filename={`k1_email_logs_${new Date()
                  .toISOString()
                  .replace(/[:.]/g, "-")}.xlsx`}
                size="sm"
                text="Download"
                className="text-sm"
              />
            </div>
          </div>
        </>
      )}

      <K1FundsModal
        isOpen={Boolean(editingRow)}
        onOpenChange={(open) => {
          if (!open) setEditingRow(null);
        }}
        funds={funds}
        selectedIds={editingFundId !== null ? [editingFundId] : []}
        selection={editingSelection}
        onApply={(selection) => {
          if (!editingRow) return;
          const nextFundId = selection.mode === "all" ? null : selection.ids[0] ?? null;
          updateEmailLogFund.mutate({
            emailLogId: asNumber(editingRow.id) ?? 0,
            fundId: nextFundId,
          });
        }}
        singleSelect
        hideAll
        showClearAction
      />
    </div>
  );
}
